import * as React from 'react'
import { Button } from 'reactstrap'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DraggableTemplateItem } from './DraggableTemplateItem'

interface EditablePromptTemplate {
  id: number
  sequence: number
  prompt: string
  options: string
  changedFields: string[]
  invalidFields: string[]
  saveState?: 'idle' | 'saving' | 'saved' | 'error'
  saveError?: string
}

interface DraggableTemplateListProps {
  templates: EditablePromptTemplate[]
  selectedSequence: number
  onSelectTemplate: (sequence: number) => void
  onEditTemplate: (template: EditablePromptTemplate) => void
  onSaveTemplate: (template: EditablePromptTemplate) => void
  onDeleteTemplate: (template: EditablePromptTemplate) => void
  onReorderTemplates: (reorderedTemplates: EditablePromptTemplate[]) => void
  onAddNewTemplate: () => void
  disabled?: boolean
}

export const DraggableTemplateList: React.FC<DraggableTemplateListProps> = ({
  templates,
  selectedSequence,
  onSelectTemplate,
  onEditTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onReorderTemplates,
  onAddNewTemplate,
  disabled = false,
}) => {
  const [localTemplates, setLocalTemplates] = React.useState<EditablePromptTemplate[]>(templates)

  // Update local state when templates prop changes
  React.useEffect(() => {
    setLocalTemplates(templates)
  }, [templates])

  const moveTemplate = React.useCallback((fromIndex: number, toIndex: number) => {
    const updatedTemplates = [...localTemplates]
    const [movedTemplate] = updatedTemplates.splice(fromIndex, 1)
    updatedTemplates.splice(toIndex, 0, movedTemplate)

    // Update sequences based on new positions
    const reorderedTemplates = updatedTemplates.map((template, index) => ({
      ...template,
      sequence: index + 1, // sequences start from 1
      changedFields: [...new Set([...template.changedFields, 'sequence'])] // Mark sequence as changed
    }))

    setLocalTemplates(reorderedTemplates)
    
    // Debounce the callback to parent to avoid excessive updates
    setTimeout(() => {
      onReorderTemplates(reorderedTemplates)
    }, 100)
  }, [localTemplates, onReorderTemplates])

  const handleSelectTemplate = (template: EditablePromptTemplate) => {
    onSelectTemplate(template.sequence)
  }

  const sortedTemplates = [...localTemplates].sort((a, b) => a.sequence - b.sequence)

  const hasUnsavedChanges = localTemplates.some(t => t.changedFields.length > 0)

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="draggable-template-list">
        <div className="template-list-header d-flex justify-content-between align-items-center mb-3">
          <div>
            <label className="form-label mb-0">Template Management:</label>
            <div className="text-muted small">
              {templates.length === 0 && 'No templates'}
              {templates.length === 1 && '1 template'}
              {templates.length > 1 && `${templates.length} templates - drag to reorder`}
            </div>
          </div>
          <Button
            color="primary"
            size="sm"
            onClick={onAddNewTemplate}
            disabled={disabled || hasUnsavedChanges}
            title={hasUnsavedChanges ? 'Save changes before adding new template' : 'Add a new template'}
          >
            <i className="fa fa-plus"></i> Add New Template
          </Button>
        </div>

        {templates.length === 0 && (
          <div className="text-center py-4 text-muted">
            <i className="fa fa-file-text-o fa-2x mb-2"></i>
            <div>No templates yet</div>
            <small>Click "Add New Template" to get started</small>
          </div>
        )}

        <div className="template-items">
          {sortedTemplates.map((template, index) => (
            <DraggableTemplateItem
              key={`${template.id}-${template.sequence}`}
              template={template}
              index={index}
              isSelected={template.sequence === selectedSequence}
              onSelect={handleSelectTemplate}
              onEdit={onEditTemplate}
              onSave={onSaveTemplate}
              onDelete={onDeleteTemplate}
              onMove={moveTemplate}
            />
          ))}
        </div>

        {hasUnsavedChanges && (
          <div className="alert alert-warning alert-sm mt-3">
            <i className="fa fa-exclamation-triangle"></i> You have unsaved changes. 
            Templates with unsaved changes are marked with <i className="fa fa-circle text-warning"></i>
          </div>
        )}
      </div>
    </DndProvider>
  )
}